import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import Typed from "typed.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faGithub } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope, faFileDownload } from "@fortawesome/free-solid-svg-icons";

const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const HeroSectionContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 40%, rgba(102, 126, 234, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 70% 60%, rgba(118, 75, 162, 0.1) 0%, transparent 50%);
    pointer-events: none;
    z-index: 1;
  }
`;

const PremiumBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  z-index: 0;

  /* Subtle geometric grid */
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  background-position: center center;
  mask-image: radial-gradient(ellipse at center, black 20%, transparent 80%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 20%, transparent 80%);
`;

const GlowingOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  animation: ${float} 10s ease-in-out infinite;
`;

const Orb1 = styled(GlowingOrb)`
  top: -10%;
  left: -10%;
  width: 50vw;
  height: 50vw;
  background: radial-gradient(circle, rgba(74, 144, 226, 0.3) 0%, transparent 70%);
  animation-delay: 0s;
`;

const Orb2 = styled(GlowingOrb)`
  bottom: -20%;
  right: -10%;
  width: 60vw;
  height: 60vw;
  background: radial-gradient(circle, rgba(144, 19, 254, 0.2) 0%, transparent 70%);
  animation-delay: -5s;
`;

const Content = styled.div`
  text-align: center;
  color: #ffffff;
  z-index: 10;
  position: relative;
  max-width: 900px;
  padding: 0 2rem;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 8vw, 5rem);
  margin-bottom: 1.5rem;
  font-weight: 800;
  font-family: "Inter", "Roboto", sans-serif;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled.h2`
  font-size: clamp(1.25rem, 4vw, 2rem);
  margin-bottom: 3rem;
  font-weight: 400;
  font-family: "Inter", "Roboto", sans-serif;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;

  span {
    color: #667eea;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 2rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

const IconLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.75rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0.75rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;

  &:hover {
    color: #667eea;
    transform: translateY(-6px) scale(1.08);
    background: rgba(102, 126, 234, 0.12);
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 12px 32px rgba(102, 126, 234, 0.25);
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    width: 3rem;
    height: 3rem;
    border-radius: 12px;
  }
`;

const HeroSection = () => {
  const el = useRef(null);

  useEffect(() => {
    const typed = new Typed(el.current, {
      strings: ["Technology Leader", "Strategic Data Analyst", "AI Engineer", "Web Developer"],
      typeSpeed: 50,
      backSpeed: 50,
      loop: true
    });

    return () => {
      typed.destroy();
    };
  }, []);

  return (
    <HeroSectionContainer>
      <PremiumBackground>
        <Orb1 />
        <Orb2 />
      </PremiumBackground>
      <Content>
        <Title>Scott Winyard</Title>
        <Subtitle>
          I'm a <span ref={el} />
        </Subtitle>
        <SocialLinks>
          <IconLink href="https://www.linkedin.com/in/scottwinyard" target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faLinkedin} />
          </IconLink>
          <IconLink href="https://github.com/jeggsy" target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faGithub} />
          </IconLink>
          <IconLink href="mailto:s.winyard@hotmail.com">
            <FontAwesomeIcon icon={faEnvelope} />
          </IconLink>
          <IconLink href="./SWCV.pdf" download>
            <FontAwesomeIcon icon={faFileDownload} />
          </IconLink>
        </SocialLinks>
      </Content>
    </HeroSectionContainer>
  );
};

export default HeroSection;